import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  User,
  Mail,
  Phone,
  MapPin,
  Home,
  MessageSquare,
  CheckCircle2,
  Loader2,
  AlertCircle,
  ArrowLeft,
} from "lucide-react";

import Section from "@/components/layout/Section";
import Container from "@/components/layout/Container";
import { saveLead } from "@/utils/saveLead";

const INITIAL_FORM = {
  name: "",
  email: "",
  phone: "",
  city: "",
  type: "",
  message: "",
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^[0-9+\-\s()]{7,}$/;

const trim = (value) => String(value ?? "").trim();

const PROPERTY_TYPES = [
  "Apartment",
  "House",
  "Cottage",
  "Villa",
];

function validate(form) {
  const errors = {};

  const name = trim(form.name);
  const email = trim(form.email);
  const phone = trim(form.phone);
  const city = trim(form.city);
  const type = trim(form.type);
  const message = trim(form.message);

  if (!name) errors.name = "Full name is required.";
  if (!email) errors.email = "Email address is required.";
  if (email && !EMAIL_REGEX.test(email)) errors.email = "Enter a valid email address.";

  if (!phone) errors.phone = "Phone number is required.";
  if (phone && !PHONE_REGEX.test(phone)) errors.phone = "Enter a valid phone number.";

  if (!city) errors.city = "City or State is required.";
  if (city && city.length < 2) errors.city = "Enter a valid city or state.";

  if (!type) errors.type = "Property type is required.";

  if (!message) errors.message = "Message is required.";
  if (message && message.length < 3) errors.message = "Please add a little more detail.";

  return errors;
}

function withTimeout(promise, ms, message = "Request timed out.") {
  let timeoutId;
  const timeout = new Promise((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error(message)), ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timeoutId));
}

export default function ContactForm() {
  const [form, setForm] = useState(INITIAL_FORM);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [errors, setErrors] = useState([]);

  const update = (key) => (e) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const selectType = (value) => {
    setForm((prev) => ({ ...prev, type: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setShowErrors(true);

    const nextErrorsMap = validate(form);
    const nextErrors = Object.values(nextErrorsMap);

    if (nextErrors.length > 0) {
      setErrors(nextErrors);
      return;
    }

    try {
      setLoading(true);
      setErrors([]);

      await withTimeout(
        saveLead({
          page: "contactus",
          leadType: "contact_form",
          airbnbLink: null,
          listingId: null,
          propertyDetails: {
            city: trim(form.city),
            type: trim(form.type),
          },
          leadDetails: {
            name: trim(form.name),
            email: trim(form.email),
            phone: trim(form.phone),
            message: trim(form.message),
          },
          source: "website_contact_form",
        }),
        12000,
        "Saving took too long. Please try again."
      );

      setSubmitted(true);
      setForm(INITIAL_FORM);
      setShowErrors(false);
      setErrors([]);
    } catch (err) {
      console.error("Lead save error:", err);
      setErrors([err?.message || "Something went wrong. Please try again."]);
      setShowErrors(true);
    } finally {
      setLoading(false);
    }
  };

  const backHome = () => {
    window.location.href = "/";
  };

  return (
    <Section background="bg-bg-surface" padding="md">
      <Container>
        <div className="grid items-start gap-12 lg:grid-cols-[1.05fr_0.95fr] lg:gap-16">
          {/* LEFT PANEL */}
          <div className="max-w-content pt-2">
            <span className="text-small font-semibold uppercase tracking-[0.2em] text-brand-primary">
              Consultation
            </span>

            <h2 className="mt-4 text-display font-extrabold leading-tight text-text-primary">
              Maximise your Airbnb revenue
            </h2>

            <p className="mt-4 max-w-reading text-body text-text-muted">
              Pricing strategy, demand insights and operations designed to increase occupancy and revenue.
            </p>

            <div className="mt-10 space-y-4 rounded-xl border border-border-soft bg-bg-light p-5 shadow-soft">
              {[
                "Revenue optimisation system",
                "Market demand insights",
                "End-to-end management support",
              ].map((item, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className="mt-1 h-1.5 w-1.5 rounded-full bg-brand-primary" />
                  <p className="text-body text-text-muted">{item}</p>
                </div>
              ))}
            </div>

            <div className="mt-8 text-small text-text-muted">
              Takes less than 30 seconds
            </div>
          </div>

          {/* RIGHT FORM */}
          <div className="relative">
            <div className="absolute inset-0 rounded-xl bg-gradient-glow blur-2xl opacity-70" />

            <AnimatePresence mode="wait">
              {!submitted ? (
                <motion.form
                  key="form"
                  onSubmit={handleSubmit}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="relative card-base p-6 md:p-8"
                  noValidate
                >
                  <div className="grid gap-4 md:grid-cols-2">
                    <TextField
                      icon={User}
                      label="Full name"
                      value={form.name}
                      onChange={update("name")}
                      autoComplete="name"
                    />

                    <TextField
                      icon={Mail}
                      label="Email address"
                      value={form.email}
                      onChange={update("email")}
                      autoComplete="email"
                      inputMode="email"
                    />

                    <TextField
                      icon={Phone}
                      label="Phone number"
                      value={form.phone}
                      onChange={update("phone")}
                      autoComplete="tel"
                      inputMode="tel"
                    />

                    <TextField
                      icon={MapPin}
                      label="City or State"
                      value={form.city}
                      onChange={update("city")}
                      autoComplete="address-level2"
                    />

                    <div className="md:col-span-2">
                      <div className="mb-3 flex items-center gap-2 text-small font-medium text-text-muted">
                        <Home className="h-4 w-4" />
                        Property type
                      </div>

                      <div className="grid gap-3 sm:grid-cols-2">
                        {PROPERTY_TYPES.map((option) => {
                          const active = form.type === option;
                          return (
                            <button
                              key={option}
                              type="button"
                              onClick={() => selectType(option)}
                              className={`
                                flex items-center justify-between rounded-xl border px-4 py-4 text-left transition duration-normal ease-zen
                                ${active
                                  ? "border-brand-primary bg-brand-light shadow-soft"
                                  : "border-border-soft bg-bg-surface hover:border-border-subtle hover:bg-bg-light"}
                              `}
                            >
                              <span className="text-body text-text-primary">{option}</span>
                              <span className={`ml-4 flex h-5 w-5 items-center justify-center rounded-full border ${active ? "border-brand-primary bg-brand-primary" : "border-border-soft bg-bg-surface"}`}>
                                {active ? <CheckCircle2 className="h-3 w-3 text-white" /> : null}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div className="md:col-span-2">
                      <TextAreaField
                        icon={MessageSquare}
                        label="Anything about your property"
                        value={form.message}
                        onChange={update("message")}
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-primary mt-6 w-full disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {loading ? (
                      <span className="flex items-center justify-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Processing...
                      </span>
                    ) : (
                      "Request consultation"
                    )}
                  </button>

                  <AnimatePresence>
                    {showErrors && errors.length > 0 ? (
                      <motion.div
                        initial={{ opacity: 0, y: -6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        className="mt-4 rounded-xl border border-border-soft bg-bg-light px-4 py-3 text-small text-text-muted"
                      >
                        <div className="mb-2 flex items-center gap-2 font-semibold text-text-primary">
                          <AlertCircle className="h-4 w-4 text-brand-primary" />
                          Please fix these fields
                        </div>
                        <ul className="space-y-1">
                          {errors.map((error, index) => (
                            <li key={index}>• {error}</li>
                          ))}
                        </ul>
                      </motion.div>
                    ) : null}
                  </AnimatePresence>

                  <div className="mt-4 text-center text-micro text-text-muted">
                    We’ll get back within 24 hours. No spam.
                  </div>
                </motion.form>
              ) : (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, scale: 0.95, y: 10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  transition={{ type: "spring", stiffness: 140, damping: 16 }}
                  className="relative overflow-hidden rounded-xl border border-border-soft bg-bg-surface p-8 text-center shadow-card md:p-10"
                >
                  <motion.div
                    initial={{ scale: 0, rotate: -12 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ type: "spring", stiffness: 180, damping: 12 }}
                    className="mx-auto flex h-16 w-16 items-center justify-center rounded-full border border-brand-primary/20 bg-brand-light"
                  >
                    <CheckCircle2 className="h-8 w-8 text-brand-primary" />
                  </motion.div>

                  <motion.h3
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="mt-5 text-section font-semibold text-text-primary"
                  >
                    Request received
                  </motion.h3>

                  <motion.p
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.16 }}
                    className="mt-2 text-body text-text-muted"
                  >
                    Our team will work shortly with you.
                  </motion.p>

                  <motion.p
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="mt-3 text-small text-brand-primary"
                  >
                    We’ll be in touch soon.
                  </motion.p>

                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.22 }}
                    className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center"
                  >
                    <button
                      type="button"
                      onClick={backHome}
                      className="btn-primary inline-flex items-center justify-center gap-2"
                    >
                      <ArrowLeft className="h-4 w-4" />
                      Back to home
                    </button>

                    <button
                      type="button"
                      onClick={() => setSubmitted(false)}
                      className="btn-outline inline-flex items-center justify-center"
                    >
                      Send another request
                    </button>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </Container>
    </Section>
  );
}

function TextField({ icon: Icon, label, className = "", ...props }) {
  return (
    <div className={`rounded-xl border border-border-soft bg-bg-surface px-4 py-4 shadow-soft transition duration-normal ease-zen hover:shadow-card ${className}`}>
      <div className="flex items-start gap-3">
        <Icon className="mt-1 h-4 w-4 flex-none text-text-muted" />
        <div className="min-w-0 flex-1">
          <label className="block text-small font-medium leading-none text-text-muted">
            {label}
          </label>
          <input
            {...props}
            placeholder=""
            className="mt-2 w-full bg-transparent text-body text-text-primary outline-none placeholder:text-transparent"
          />
        </div>
      </div>
    </div>
  );
}

function TextAreaField({ icon: Icon, label, className = "", ...props }) {
  return (
    <div className={`rounded-xl border border-border-soft bg-bg-surface px-4 py-4 shadow-soft transition duration-normal ease-zen hover:shadow-card ${className}`}>
      <div className="flex items-start gap-3">
        <Icon className="mt-1 h-4 w-4 flex-none text-text-muted" />
        <div className="min-w-0 flex-1">
          <label className="block text-small font-medium leading-none text-text-muted">
            {label}
          </label>
          <textarea
            {...props}
            rows={5}
            placeholder=""
            className="mt-2 w-full resize-none bg-transparent text-body text-text-primary outline-none placeholder:text-transparent"
          />
        </div>
      </div>
    </div>
  );
}